import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ProGroupsWeb } from '@pro-groups/shared/web';
import { ApiService, appConfig, resetStoreInitialization } from '@pro-groups/shared';
import type { NavigationTab } from '@pro-groups/shared';
import { FetchHttpAdapter } from './services/FetchHttpAdapter';
import { WebSignInView, type SignInResult } from './components/WebSignInView';
import { WebAdminSignInView } from './components/WebAdminSignInView';
import { SecureAdministrationView } from './components/SecureAdministrationView';
import { ShareReportView } from './components/ShareReportView';
import { AutoSignOutManager } from './components/AutoSignOutManager';
import { checkStoredSession, clearSession, getStoredAccessType } from './services/WebAuthService';
import { webStorageAdapter } from './storage/WebStorageAdapter';
import { syncDirtyProfiles } from '@pro-groups/shared/utils/syncBeforeSignOut';
import { resetMilestoneSyncSession } from '@pro-groups/shared';
import { AccountUserStorage } from '@pro-groups/shared/storage/AccountUserStorage';
import { getAuthEntryConfig, resolveAuthEntryModeFromPath, resolveSkinTypeFromPath, resolveTokenRoute, routeAcceptsStoredAccessType } from './auth/authEntry';
import './App.css';

interface ErrorBoundaryState { hasError: boolean; error?: Error }

class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: 'monospace', color: 'red' }}>
          <h2>App Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error?.message}
            {'\n'}
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth state held at the app level, outside StoreProvider, so the sign-in page
// can exist before the shared store is mounted.
interface AppAuthState {
  checked:         boolean;   // true once localStorage has been inspected
  isAuthenticated: boolean;
  email?:          string;
}

function App() {
  const tokenRoute = useMemo(() => resolveTokenRoute(window.location.pathname), []);
  const authMode = useMemo(() => resolveAuthEntryModeFromPath(window.location.pathname), []);
  const authEntry = useMemo(() => getAuthEntryConfig(authMode), [authMode]);
  const signInSkinType = useMemo(
    () => resolveSkinTypeFromPath(window.location.pathname, window.location.hostname, authMode),
    [authMode]
  );

  const apiService = useMemo(() => {
    // appConfig resolves to LOCALHOST_CONFIG in development → http://localhost:5162/api
    const apiBaseUrl = appConfig.getConfig().api.baseUrl;
    // Strip /api suffix to get the server root — FetchHttpAdapter prepends this to
    // every relative endpoint path (e.g. /api/Authentication/login).
    // In dev, this produces http://localhost:5162; the Vite proxy in vite.config.ts
    // also maps /api/* → http://localhost:5162 as a CORS safety-net.
    // All requests are now clearly visible in browser devtools targeting port 5162.
    const serverBaseUrl = apiBaseUrl.replace(/\/api$/, '');
    // Every route — user-based sign-in forms and token routes alike — talks to the same
    // /api/Authentication/* and /api/services/* endpoints. The backend has no per-route URL
    // prefix: which form is shown and the AccessType/SkinType sent are what differ (see
    // auth/authEntry.ts), and the JWT carries the AccessType afterwards.
    const scopedHttpAdapter = new FetchHttpAdapter(serverBaseUrl);
    return new ApiService(`${serverBaseUrl}/api`, scopedHttpAdapter);
  }, []);

  const [auth, setAuth] = useState<AppAuthState>({ checked: false, isAuthenticated: false });
  const [initialTab, setInitialTab] = useState<NavigationTab>('Clients');
  const [upgradeSuccessBanner, setUpgradeSuccessBanner] = useState(false);
  // Set when the user was signed out because the session expired; shown on the sign-in page.
  const [sessionNotice, setSessionNotice] = useState<string | undefined>(undefined);
  // Holds the Stripe CheckoutId to verify after auth is confirmed and stores are ready.
  // Detected during init() but deferred until auth.isAuthenticated is true so that the
  // token is valid and userStore is available for 401 refresh handling.
  const pendingStripeVerifyRef = useRef<string | null>(null);

  // On first render, check whether a session token is already stored.
  // Also detect Stripe return params (?upgrade=success&session_id=... or ?upgrade=cancel)
  // and clean the URL immediately so they don't persist on reload.
  useEffect(() => {
    async function init() {
      let stored = checkStoredSession();

      // A dedicated sign-in route (/channel, /account, /office) only accepts a stored
      // session of its own AccessType. Any other stored session (e.g. a Pro session on
      // /channel) is signed out here — the same clean-up as the sign-out button — so that route's
      // sign-in form is shown instead of silently reusing the wrong identity. The base/skin
      // routes and token routes are unaffected.
      if (stored && !tokenRoute && !routeAcceptsStoredAccessType(authEntry, getStoredAccessType())) {
        // handleSignOut syncs dirty profiles first, which needs the stored token to be valid.
        apiService.setAuthToken(stored.token);
        await handleSignOut();
        stored = null;
      }

      if (stored && !tokenRoute) {
        // Re-hydrate the API service with the stored token so any immediate
        // background requests are authenticated before ChildNavWeb mounts.
        //
        // NEVER on a token route (/secure, /remote, /link, /share): that flow's own
        // scoped session token is applied by resolveSecureAdministration, and a leftover
        // SRA `auth_token` in localStorage (from a prior sign-in on this browser) would
        // otherwise clobber it on this shared apiService instance — the respondent's
        // admin_response_submit would then go out under the wrong (SRA) identity and 401.
        apiService.setAuthToken(stored.token);
      }

      // Detect Stripe return URL params
      const params = new URLSearchParams(window.location.search);
      const upgradeParam = params.get('upgrade');
      if (upgradeParam) {
        // Clean the URL immediately — prevents params reappearing on back/forward nav
        window.history.replaceState(null, '', '/');

        if (upgradeParam === 'cancel') {
          // Signal AccountView to re-open UpgradeView on its next mount
          (globalThis as any).__upgradeReturnCancel = true;
          setInitialTab('Account');
        } else if (upgradeParam === 'success') {
          const sessionId = params.get('session_id');
          console.log('🛒 App: Stripe success return detected, deferring verify for sessionId:', sessionId);
          setInitialTab('Account');
          if (sessionId && stored) {
            // Defer verify — stores not ready yet at this point; the second useEffect
            // below fires once auth.isAuthenticated is true and ChildNavWeb is mounted.
            pendingStripeVerifyRef.current = sessionId;
          }
        }
      }

      // Detect the Subscription tab's own billpay_init_create / billpay_init_session
      // return (distinct from the "upgrade" premium-tier flow above — this is the
      // practice-level Account > Subscription billing flow). SubscriptionView itself
      // reads __billpayCheckoutSessionId and calls account_verify_stripe_checkout once
      // it mounts; ProGroupsShell reads __proGroupsPendingAccountSubsection to land back
      // on the Subscription tab specifically rather than just the Account section.
      const billpayParam = params.get('billpay');
      if (billpayParam) {
        window.history.replaceState(null, '', '/');
        (globalThis as any).__proGroupsPendingAccountSubsection = 'Subscription';
        if (billpayParam === 'success') {
          const sessionId = params.get('session_id');
          if (sessionId) {
            (globalThis as any).__billpayCheckoutSessionId = sessionId;
          }
        }
        setInitialTab('Account');
      }

      setAuth({
        checked:         true,
        isAuthenticated: !!stored,
        email:           stored?.email,
      });
    }
    init();
  }, [apiService, authEntry]);

  // Verify a pending Stripe checkout session once auth is confirmed and stores are ready.
  // This runs after ProGroupsWeb mounts so __proGroupsStores is populated and userStore
  // is available for processServiceRequestWithRefresh to handle 401 token refreshes.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const sessionId = pendingStripeVerifyRef.current;
    if (!sessionId) return;
    pendingStripeVerifyRef.current = null;

    const runVerify = async () => {
      console.log('🛒 App: Verifying Stripe checkout — RequestType: account_verify_stripe_checkout, CheckoutId:', sessionId);
      try {
        const globalStores = ((globalThis as any).__proGroupsStores ?? (globalThis as any).__childNavStores) as Record<string, any> | undefined;
        const activeUserStore = globalStores?.useUserStore ?? undefined;
        const response = await apiService.processServiceRequestWithRefresh({
          RequestType: 'account_verify_stripe_checkout',
          Payload: JSON.stringify({ BPProcId: '2', CheckoutId: sessionId }),
        }, 'sync', {
          userStore: activeUserStore,
        });
        console.log('🛒 App: account_verify_stripe_checkout response:', JSON.stringify(response));
        if (response?.success) {
          // Parse AccountStripeCheckoutVerifyResponseDto from payload
          let verifyDto: { CustomerId?: string; SubscriptionId?: string; ProductList?: string } = {};
          if (typeof response.payload === 'string' && response.payload) {
            verifyDto = JSON.parse(response.payload);
          } else if (response.payload) {
            verifyDto = response.payload as unknown as typeof verifyDto;
          }
          console.log('🛒 App: verify dto:', verifyDto);

          // If SubscriptionId is present, mark account as premium in local storage
          if (verifyDto.SubscriptionId) {
            // Update in-memory config immediately so already-mounted views see premium
            appConfig.updateConfig({ tier: 'premium' });
            try {
              const userState = activeUserStore?.getState?.();
              const accountId = userState?.session?.user?.familyId
                || userState?.session?.familyAccount?.familyId
                || '';
              if (accountId) {
                const accountUserStorage = new AccountUserStorage(webStorageAdapter);
                const dto = await accountUserStorage.getAccountGetInfo(accountId);
                if (dto) {
                  const updated = {
                    ...dto,
                    IsPremium: '1',
                    CustomerId: verifyDto.CustomerId ?? dto.CustomerId,
                    SubscriptionId: verifyDto.SubscriptionId,
                    ProductList: verifyDto.ProductList ?? dto.ProductList,
                  };
                  await accountUserStorage.saveAccountGetInfo(accountId, updated);
                  console.log('🛒 App: Local account info updated — IsPremium=1, SubscriptionId:', verifyDto.SubscriptionId);
                }
              }
            } catch (storageErr) {
              console.warn('⚠️ App: Failed to update local account storage after verify:', storageErr);
            }
            setUpgradeSuccessBanner(true);
          } else {
            console.warn('⚠️ App: Stripe verify succeeded but SubscriptionId is empty — premium not applied');
          }
        } else {
          console.warn('⚠️ App: account_verify_stripe_checkout returned failure:', response?.message);
        }
      } catch (e) {
        console.warn('⚠️ App: Stripe session verify failed:', e);
      }
    };
    runVerify();
  }, [auth.isAuthenticated, apiService]);

  const handleSignIn = (result: SignInResult) => {
    // Token is already persisted to localStorage by WebAuthService.loginWithCode /
    // authenticateWithPasskey.  Re-hydrate apiService with the new token immediately so
    // that ChildNavWeb / StoreProvider mounts with a fully authenticated API client —
    // without this, apiService.authToken remains null from the preceding sign-out and
    // every backend call inside ChildNavWeb (including DebugView) sees "no auth token".
    if (result.token) {
      apiService.setAuthToken(result.token);
    }
    setSessionNotice(undefined);
    setAuth({ checked: true, isAuthenticated: true, email: result.email });
  };

  const handleSignOut = async (options?: { expired?: boolean }) => {
    // Sync dirty profiles while the auth token is still valid. Skipped when the session already
    // expired — the token is dead, so the sync could only fail (or hang on retries).
    if (!options?.expired) {
      try {
        await syncDirtyProfiles();
      } catch (syncError) {
        // A failed sync must never prevent signing out.
        console.warn('App: syncDirtyProfiles failed during sign-out', syncError);
      }
    }
    // Reset the module-level milestone-sync guard so the next sign-in always
    // re-extracts milestone/concern definitions from bundled content.  Without
    // this, clearing localStorage and signing back in leaves milestone storage
    // empty because the guard thinks the sync already ran this session.
    resetMilestoneSyncSession();
    // 1. Properly reset the Zustand user-store session so the cached in-memory session
    //    (isAuthenticated=true, valid lastActivityTime) doesn't cause isSessionValid() to
    //    return true on the next sign-in, which would skip restoreSessionFromStorage and
    //    leave the API service without a token.
    try {
      const globalStores = (globalThis as any).__proGroupsStores ?? (globalThis as any).__childNavStores;
      if (globalStores?.useUserStore?.getState?.()?.logout) {
        await globalStores.useUserStore.getState().logout();
      }
    } catch { /* ignore logout errors */ }
    // 2. Clear the store-initialization guard so ChildNavWeb gets fresh stores next sign-in.
    resetStoreInitialization();
    // 3. Wipe all persisted store data from IndexedDB (and localStorage fallback) so the
    //    next account that signs in starts with a clean slate.  Raw localStorage.removeItem()
    //    does NOT touch IndexedDB which is the primary Zustand persist backend, so we must
    //    use webStorageAdapter here.
    const STORE_KEYS = [
      'child-nav-profiles',
      'child-nav-milestones',
      'child-nav-concerns',
      'child-nav-visits',
      'child-nav-actions',
      'child-nav-content',
    ];
    await Promise.all(STORE_KEYS.map((k) => webStorageAdapter.removeItem(k).catch(() => {})));
    // 4. Clear localStorage session keys and null out the apiService token.
    clearSession();
    apiService.setAuthToken(null);
    setAuth({ checked: true, isAuthenticated: false });
  };

  // Session expired (token refresh rejected): sign out and return to the sign-in page with a
  // notice. Not registered on token routes (/secure /link /remote /share) — there an auth failure
  // should just show on the page.
  const handleSignOutRef = useRef(handleSignOut);
  handleSignOutRef.current = handleSignOut;
  const isAuthenticatedRef = useRef(auth.isAuthenticated);
  isAuthenticatedRef.current = auth.isAuthenticated;
  useEffect(() => {
    if (tokenRoute) {
      return;
    }
    apiService.setSessionExpiredHandler(() => {
      if (!isAuthenticatedRef.current) {
        return;
      }
      setSessionNotice('Your session expired. Please sign in again.');
      void handleSignOutRef.current({ expired: true });
    });
    return () => apiService.setSessionExpiredHandler(null);
  }, [apiService, tokenRoute]);

  // Expose sign-out so ChildNavWeb can trigger it (e.g. from Settings / Account view).
  // We put it on globalThis so the shared package can reach it without a prop-drill.
  useEffect(() => {
    (globalThis as any).__webSignOut = handleSignOut;
    return () => { delete (globalThis as any).__webSignOut; };
  });

  // While we're still reading localStorage, render nothing (avoids flash of sign-in).
  if (!auth.checked && !tokenRoute) {
    return null;
  }

  return (
    <ErrorBoundary>
      {tokenRoute ? (
        tokenRoute.mode === 'share' ? (
          // /share (AccessType 43) isn't an administration flow — it's a verify-then-view
          // flow for a report shared via secure email, handled entirely by its own view.
          <ShareReportView apiService={apiService} token={tokenRoute.token} />
        ) : (
          // No onExit here by design — a secure-login/session error on a link-based route
          // should stay on this page and show the error, not bounce the respondent to the
          // SRA login screen (they were never signing in to begin with).
          <SecureAdministrationView
            apiService={apiService}
            mode={tokenRoute.mode}
            token={tokenRoute.token}
          />
        )
      ) : auth.isAuthenticated ? (
        <>
          <ProGroupsWeb apiService={apiService} storage={webStorageAdapter} initialTab={initialTab} onSignOut={handleSignOut} />
          <AutoSignOutManager onSignOut={() => void handleSignOut()} apiService={apiService} />
          {upgradeSuccessBanner && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
              backgroundColor: '#059669', color: '#fff',
              padding: '14px 20px', textAlign: 'center',
              fontFamily: 'system-ui, sans-serif', fontSize: 15, fontWeight: 600,
              boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            }}>
              🎉 Welcome to Premium! Your account has been upgraded. You now have access to all premium features including separate Fine Motor &amp; Gross Motor milestone tracking. You should update these new milestone domains at your earliest convenience.
              <button
                onClick={() => setUpgradeSuccessBanner(false)}
                style={{
                  marginLeft: 16, background: 'transparent', border: '1px solid rgba(255,255,255,0.6)',
                  color: '#fff', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 13,
                }}
              >
                Dismiss
              </button>
            </div>
          )}
        </>
      ) : authEntry.form === 'standard' ? (
        <WebSignInView
          apiService={apiService}
          accessType={authEntry.accessType}
          skinType={signInSkinType}
          storage={webStorageAdapter}
          passkeyEnabled={authEntry.passkeyEnabled}
          policyNotice={authEntry.policyNotice}
          sessionNotice={sessionNotice}
          onSignIn={handleSignIn}
          allowSignUp={authEntry.mode === 'pro'}
        />
      ) : (
        <WebAdminSignInView
          apiService={apiService}
          accessType={authEntry.accessType as '20' | '21'}
          skinType={signInSkinType}
          title={authEntry.title ?? 'Administration Sign In'}
          subtitle={authEntry.subtitle ?? 'Use your admin credentials and security code.'}
          policyNotice={authEntry.policyNotice}
          sessionNotice={sessionNotice}
          onSignIn={handleSignIn}
        />
      )}
    </ErrorBoundary>
  );
}

export default App;
